import React, { useState } from 'react';
import './CancelBooking.css';

function CancelBooking({ onClose }) {
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');
  const [cancelled, setCancelled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const API_URL = '/api/bookings';

  const handleLookup = async (e) => {
    e.preventDefault();
    setError('');
    setBooking(null);
    setIsLoading(true);

    try {
      // First check localStorage for full booking details
      const localBookings = JSON.parse(localStorage.getItem('bookings') || '[]');
      const localFound = localBookings.find(
        b => b.confirmationNumber.toLowerCase() === confirmationNumber.trim().toLowerCase()
      );

      if (localFound) {
        setBooking(localFound);
        setIsLoading(false);
        return;
      }

      // If not in localStorage, check API for confirmation number
      const response = await fetch(API_URL);
      if (response.ok) {
        const data = await response.json();
        const apiFound = data.bookings.find(
          b => b.confirmationNumber?.toLowerCase() === confirmationNumber.trim().toLowerCase()
        );

        if (apiFound) {
          // API only has limited data, create partial booking object
          setBooking({
            confirmationNumber: apiFound.confirmationNumber,
            date: apiFound.date,
            time: apiFound.time,
            service: 'Oil Change Service',
            vehicleYear: '',
            vehicleMake: '',
            vehicleModel: ''
          });
          setIsLoading(false);
          return;
        }
      }

      setError('Booking not found. Please check your confirmation number and try again.');
    } catch (err) {
      console.error('Lookup error:', err);
      setError('Unable to look up booking. Please try again or contact us.');
    }
    setIsLoading(false);
  };

  const handleCancel = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Cancel via API
      const response = await fetch(API_URL, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          confirmationNumber: booking.confirmationNumber
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel booking');
      }

      // Also remove from localStorage
      const existingBookings = JSON.parse(localStorage.getItem('bookings') || '[]');
      const updatedBookings = existingBookings.filter(
        b => b.confirmationNumber !== booking.confirmationNumber
      );
      localStorage.setItem('bookings', JSON.stringify(updatedBookings));

      setCancelled(true);
    } catch (err) {
      console.error('Cancel error:', err);
      setError('Unable to cancel booking. Please try again or contact us.');
    }
    setIsLoading(false);
  };

  const isWithin12Hours = () => {
    if (!booking) return false;
    const bookingDateTime = new Date(`${booking.date} ${booking.time}`);
    const now = new Date();
    const hoursUntil = (bookingDateTime - now) / (1000 * 60 * 60);
    return hoursUntil < 12;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content cancel-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>

        {cancelled ? (
          <div className="cancel-success">
            <div className="success-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h2>Booking Cancelled</h2>
            <p>Your booking <strong>{booking.confirmationNumber}</strong> has been cancelled.</p>
            <p className="cancel-note">
              If you have any questions, please contact us at{' '}
              <a href="tel:9168188494">(916) 818-8494</a>
            </p>
            <button className="btn-close-modal" onClick={onClose}>Close</button>
          </div>
        ) : !booking ? (
          <div className="cancel-lookup">
            <h2>Cancel Booking</h2>
            <p>Enter your confirmation number to look up your booking.</p>

            <form onSubmit={handleLookup}>
              <div className="form-group">
                <label htmlFor="confirmationNumber">Confirmation Number</label>
                <input
                  type="text"
                  id="confirmationNumber"
                  value={confirmationNumber}
                  onChange={(e) => setConfirmationNumber(e.target.value)}
                  placeholder="e.g., LL-XXXXXX-XXXX"
                  required
                />
              </div>

              {error && <p className="error-message">{error}</p>}

              <button type="submit" className="btn-lookup" disabled={isLoading}>
                {isLoading ? 'Looking up...' : 'Look Up Booking'}
              </button>
            </form>

            <div className="cancel-help">
              <p>Can't find your confirmation number?</p>
              <p>Call us at <a href="tel:9168188494">(916) 818-8494</a></p>
            </div>
          </div>
        ) : (
          <div className="cancel-confirm">
            <h2>Cancel This Booking?</h2>

            <div className="booking-details-card">
              <div className="detail-row">
                <span className="label">Confirmation:</span>
                <span className="value">{booking.confirmationNumber}</span>
              </div>
              <div className="detail-row">
                <span className="label">Service:</span>
                <span className="value">{booking.service}</span>
              </div>
              <div className="detail-row">
                <span className="label">Date:</span>
                <span className="value">{booking.date}</span>
              </div>
              <div className="detail-row">
                <span className="label">Time:</span>
                <span className="value">{booking.time}</span>
              </div>
              <div className="detail-row">
                <span className="label">Vehicle:</span>
                <span className="value">
                  {booking.vehicleYear} {booking.vehicleMake} {booking.vehicleModel}
                </span>
              </div>
            </div>

            {isWithin12Hours() && (
              <div className="late-warning">
                <strong>Late Cancellation Notice:</strong>
                <p>
                  This booking is within 12 hours of the scheduled time.
                  A $30 cancellation fee may apply per our{' '}
                  <a href="/cancellation" target="_blank" rel="noopener noreferrer">
                    cancellation policy
                  </a>.
                </p>
              </div>
            )}

            {error && <p className="error-message">{error}</p>}

            <div className="cancel-actions">
              <button className="btn-back-lookup" onClick={() => setBooking(null)} disabled={isLoading}>
                Back
              </button>
              <button className="btn-confirm-cancel" onClick={handleCancel} disabled={isLoading}>
                {isLoading ? 'Cancelling...' : 'Yes, Cancel Booking'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CancelBooking;
